import { Component, OnDestroy, OnInit, HostListener, ChangeDetectionStrategy } from "@angular/core";
import { Router } from "@angular/router";
import { Subject, combineLatest } from "rxjs";
import { takeUntil, startWith } from "rxjs/operators";
import { AuthService } from "../../services/auth.service";
import { LanguageService } from "../../services/language.service";
import { TranslateService } from "@ngx-translate/core";
import { ThemeService } from "../../core/theme/theme.service";
import {
  BrandingService,
  LocalizedStudentBranding,
  localizeStudentBranding,
  emptyStudentPortalBranding,
} from "../../core/branding/branding.service";
import { environment } from "../../../environments/environment";

@Component({
    selector: "app-header",
    templateUrl: "./header.component.html",
    styleUrls: ["./header.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HeaderComponent implements OnInit, OnDestroy {
  isLoggedIn = false;
  currentUser: any = null;
  isDropdownOpen = false;
  isLangDropdownOpen = false;
  currentLanguage = "en";
  showTestLabel = environment.production === false;
  siteLogoSrc = this.themeService.getSiteLogoUrl();
  branding: LocalizedStudentBranding = localizeStudentBranding(
    emptyStudentPortalBranding(),
    "en"
  );
  private readonly destroy$ = new Subject<void>();

  languages = [
    { code: "en", name: "English", nativeName: "English" },
    { code: "es", name: "Spanish", nativeName: "español" },
  ];

  constructor(
    private authService: AuthService,
    private router: Router,
    private languageService: LanguageService,
    private translate: TranslateService,
    private themeService: ThemeService,
    private brandingService: BrandingService
  ) {}

  ngOnInit(): void {
    this.themeService.siteLogoUrl$
      .pipe(takeUntil(this.destroy$))
      .subscribe((url) => {
        this.siteLogoSrc = url;
      });

    this.currentLanguage = this.languageService.getCurrentLanguage();

    combineLatest([
      this.brandingService.branding$,
      this.translate.onLangChange.pipe(startWith({ lang: this.currentLanguage })),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([raw, langEvent]) => {
        this.currentLanguage = langEvent.lang || this.currentLanguage;
        this.branding = localizeStudentBranding(raw, this.currentLanguage);
      });

    this.authService.isLoggedIn$.pipe(takeUntil(this.destroy$)).subscribe((loggedIn) => {
      this.isLoggedIn = loggedIn;
    });

    this.authService.currentUser$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
      this.currentUser = user;
    });
  }

  get logoAlt(): string {
    const title = this.branding.programTitle?.trim();
    const subtitle = this.branding.programSubtitle?.trim();
    if (title && subtitle) {
      return `${title} ${subtitle}`;
    }
    return title || subtitle || "Logo";
  }

  toggleDropdown(): void {
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  closeDropdown(): void {
    this.isDropdownOpen = false;
  }

  toggleLangDropdown(): void {
    this.isLangDropdownOpen = !this.isLangDropdownOpen;
  }

  closeLangDropdown(): void {
    this.isLangDropdownOpen = false;
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest(".dropdown")) {
      this.closeDropdown();
    }
    if (!target.closest(".lang-dropdown")) {
      this.closeLangDropdown();
    }
  }

  logout(): void {
    this.authService.logout();
    this.closeDropdown();
    this.router.navigate(["/"]);
  }

  toggleLanguage(): void {
    const newLanguage = this.currentLanguage === "en" ? "es" : "en";
    this.selectLanguage(newLanguage);
  }

  selectLanguage(languageCode: string): void {
    if (languageCode !== this.currentLanguage) {
      this.languageService.useLanguage(languageCode);
      this.currentLanguage = languageCode;
    }
    this.closeLangDropdown();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
